// API service layer for communicating with backend APIs

// Backend API base URL - use this + path for web APIs (e.g. path: '/web/instant-request-list')
const BACKEND_API_BASE = 'https://backend.bsbinfotrade.com/api';

// Freight API base URL - all freight endpoints use this + /api/name (e.g. /api/quote, /api/web/freight-request)
const FREIGHT_API_BASE = 'https://freight.bsbfortrade.com';

// Build full URL for backend: BACKEND_API_BASE + path (path should be like '/web/...')
const getBackendApiUrl = (path) => {
  const p = (path || '').replace(/^\//, '');
  const base = (BACKEND_API_BASE || '').replace(/\/$/, '');
  return p ? `${base}/${p}` : base;
};

// Helper function to get API base URL (for legacy/proxy use)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (process.env.NODE_ENV === 'development') {
    return '';
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

// Helper function to safely parse JSON response (handles HTML errors)
const safeJsonParse = async (response) => {
  const contentType = response.headers.get('content-type');
  const text = await response.text();
  
  // Check if response is HTML (404 page or error page)
  if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html') || text.trim().startsWith('<!doctype')) {
    console.error('❌ Server returned HTML instead of JSON. Node.js server is not running!');
    console.error('Response preview:', text.substring(0, 300));
    throw new Error('Server returned HTML - Node.js server is not running');
  }
  
  // Check content type
  if (!contentType || !contentType.includes('application/json')) {
    console.warn('⚠️ Response is not JSON. Content-Type:', contentType);
  }
  
  try {
    return JSON.parse(text);
  } catch (error) {
    console.error('❌ Failed to parse JSON response');
    console.error('Response text:', text.substring(0, 500));
    throw new Error('Invalid JSON response from server');
  }
};

export const apiService = {

  // Polymer Live Rate API - Direct call to live API
  async getPolymerLiveRate() {
    try {
      // Use the live API directly
      const apiUrl = 'https://backend.bsbinfotrade.com/api/web/polymer_live_rate';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
          return { 
            success: false, 
            data: [],
            error: 'Server not running'
          };
        }
        try {
          const errorJson = JSON.parse(errorText);
          return { 
            success: false, 
            data: errorJson.data || [],
            error: errorJson.message || errorJson.error || 'API error'
          };
        } catch (e) {
          return { success: false, data: [], error: 'API error' };
        }
      }

      const data = await safeJsonParse(response);
      
      // Handle the API response structure: { status: 200, message: "...", data: [...] }
      let dataArray = [];
      
      // Priority 1: Check for status 200 and data array (Postman response format)
      if (data && typeof data === 'object') {
        // Check for { status: 200, data: [...] } format
        if (data.status === 200 && data.data && Array.isArray(data.data)) {
          dataArray = data.data;
        } 
        // Check for { data: [...] } format (without status)
        else if (data.data && Array.isArray(data.data)) {
          dataArray = data.data;
        } 
        // Check if response itself is an array
        else if (Array.isArray(data)) {
          dataArray = data;
        } 
        // Search for any array property
        else {
          for (const key in data) {
            if (Array.isArray(data[key])) {
              dataArray = data[key];
              break;
            }
          }
        }
      }
      
      // If no data found
      if (dataArray.length === 0) {
        return { 
          success: false, 
          data: [],
          error: data?.message || 'No data found in response'
        };
      }
      
      return { success: true, data: dataArray };
    } catch (error) {
      console.error('Error fetching polymer live rate:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { 
          success: false, 
          data: [],
          error: 'Server not running',
          message: 'Node.js server is not running. Please contact support.'
        };
      }
      return { success: false, data: [] };
    }
  },


  // IRM API - Instant Request List (https://backend.bsbinfotrade.com/api/web/instant-request-list)
  async getPolymerLiveDemand() {
    try {
      const apiUrl = getBackendApiUrl('/web/instant-request-list');

      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
          console.error('❌ IRM API: Server returned HTML.');
          return { success: false, data: [], error: 'Server error' };
        }
        try {
          const err = JSON.parse(errorText);
          return { success: false, data: err.data || [], error: err.message || err.error };
        } catch {
          return { success: false, data: [] };
        }
      }

      const data = await safeJsonParse(response);
      
      // Handle the API response structure: { message: "List successfully", data: [...] }
      if (data && data.data && Array.isArray(data.data)) {
        return {
          success: true,
          data: data.data
        };
      } else if (data && Array.isArray(data)) {
        return {
          success: true,
          data: data
        };
      } else if (data && typeof data === 'object') {
        // Try to find any array property
        for (const key in data) {
          if (Array.isArray(data[key])) {
            return {
              success: true,
              data: data[key]
            };
          }
        }
      }
      
      return { success: false, data: [] };
    } catch (error) {
      console.error('Error fetching polymer live demand:', error);
      return { success: false, data: [] };
    }
  },

  // Calculate Logistics (freight quote - uses freight.bsbfortrade.com)
  async calculateLogistics(loadingStation, unloadingStation, quantity = '') {
    try {
      const baseUrl = FREIGHT_API_BASE;
      const apiUrl = `${baseUrl}/api/quote`;
      const q = Number(quantity);
      if (quantity === '' || quantity == null || Number.isNaN(q) || q < 10 || q > 45) {
        return { success: false, message: 'quantity must be 10–45 MT' };
      }
      const body = {
        loading: String(loadingStation || '').trim(),
        unloading: String(unloadingStation || '').trim(),
        quantity: q,
      };
      if (!body.loading || !body.unloading) {
        return { success: false, message: 'loading and unloading are required' };
      }
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await safeJsonParse(response);
      return data;
    } catch (error) {
      console.error('Error calculating logistics:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { 
          success: false, 
          message: 'Server is not responding. Please contact support.',
          error: 'Server not running'
        };
      }
      return { success: false, message: 'Error calculating freight. Please try again.' };
    }
  },

  // Calculate NBFC - Simple Interest Calculator
  async calculateNBFC(amount, days) {
    try {
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/calculate-nbfc`;
      
      console.log('Calling NBFC calculator API:', apiUrl);
      console.log('Request data:', { amount, days });
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'omit',
          signal: controller.signal,
          body: JSON.stringify({
            amount: amount,
            days: days
          })
        });

        clearTimeout(timeoutId);
        console.log('NBFC API response status:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('NBFC API error response:', errorText.substring(0, 200));
          
          // Check if response is HTML (server not running)
          if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
            console.error('❌ Server returned HTML instead of JSON. Node.js server is not running!');
            return { 
              success: false, 
              message: 'Server is not responding. Using local calculation.',
              error: 'Server not running'
            };
          }
          
          return { 
            success: false, 
            message: `Server error (${response.status}). Using local calculation.` 
          };
        }

        const data = await safeJsonParse(response);
        console.log('NBFC calculation result:', data);
        return data;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.error('❌ Request timeout - server not responding');
          return { 
            success: false, 
            message: 'Request timeout. Using local calculation.',
            error: 'Timeout'
          };
        }
        throw fetchError;
      }
    } catch (error) {
      console.error('Error calculating NBFC:', error);
      if (error.message.includes('HTML') || error.message.includes('not running') || error.message.includes('Failed to fetch')) {
        return { 
          success: false, 
          message: 'Server is not responding. Using local calculation.',
          error: 'Server not running'
        };
      }
      return { 
        success: false, 
        message: 'Error calculating interest. Using local calculation.',
        error: error.message
      };
    }
  },

  // Send Credit Calculation Results via Email
  async sendCreditCalculationEmail(email, mobile, calculationData) {
    try {
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/web/send-credit-calculation`;
      
      console.log('Sending credit calculation email:', { email, mobile });
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify({
          email: email,
          mobile: mobile || '',
          amount: calculationData.principal || calculationData.amount,
          days: calculationData.days,
          interest: calculationData.interest,
          total_amount: calculationData.total_amount,
          principal: calculationData.principal || calculationData.amount
        })
      });

      console.log('Send email API response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Send email API error response:', errorText.substring(0, 200));
        return { 
          success: false, 
          message: `Server error (${response.status}). Please try again.` 
        };
      }

      const data = await safeJsonParse(response);
      console.log('Send email result:', data);
      return data;
    } catch (error) {
      console.error('Error sending credit calculation email:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { 
          success: false, 
          message: 'Server is not responding. Please contact support.',
          error: 'Server not running'
        };
      }
      return { 
        success: false, 
        message: 'Error sending email. Please try again.',
        error: error.message
      };
    }
  },

  // Submit Contact Form (https://backend.bsbinfotrade.com/api/web/contactUs)
  // Payload: full_name, mobile, subject, message, email
  async submitContact(formData) {
    try {
      const apiUrl = getBackendApiUrl('/web/contactUs');
      
      const jsonData = {};
      for (let [key, value] of formData.entries()) {
        jsonData[key] = value.toString().trim();
      }
      // Support both 'name' and 'full_name' from form
      const fullName = jsonData.full_name || jsonData.name || '';
      const email = jsonData.email || '';
      const mobile = (jsonData.mobile || '').replace(/\D/g, '');
      const subject = jsonData.subject || '';
      const message = jsonData.message || '';

      if (!fullName.length) {
        return { success: false, message: 'Full name is required' };
      }
      if (!email.length) {
        return { success: false, message: 'Email is required' };
      }
      if (mobile.length !== 10) {
        return { success: false, message: 'Valid 10-digit mobile number is required' };
      }
      if (!message.length) {
        return { success: false, message: 'Message is required' };
      }

      const finalSubject = subject.length ? subject : 'General Inquiry';
      const payload = {
        full_name: fullName,
        email,
        mobile,
        subject: finalSubject,
        message
      };

      console.log('📧 Contact API payload:', payload);
      
      const submitFormData = new FormData();
      submitFormData.append('full_name', payload.full_name);
      submitFormData.append('email', payload.email);
      submitFormData.append('mobile', payload.mobile);
      submitFormData.append('subject', payload.subject);
      submitFormData.append('message', payload.message);
      
      let response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Accept': 'application/json' },
        credentials: 'omit',
        body: submitFormData
      });
      
      if (!response.ok && (response.status === 400 || response.status === 422)) {
        const errorText = await response.text();
        console.log('📧 FormData failed, retrying as JSON...', errorText.substring(0, 200));
        response = await fetch(apiUrl, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify(payload)
        });
      }
      
      if (!response.ok && (response.status === 400 || response.status === 422)) {
        const errorText = await response.text();
        console.log('📧 JSON failed, retrying as form-urlencoded...', errorText.substring(0, 200));
        const params = new URLSearchParams();
        params.append('full_name', payload.full_name);
        params.append('email', payload.email);
        params.append('mobile', payload.mobile);
        params.append('subject', payload.subject);
        params.append('message', payload.message);
        response = await fetch(apiUrl, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
          credentials: 'omit',
          body: params.toString()
        });
      }

      console.log('📧 Contact form response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('📧 Contact form error:', errorText);
        console.error('📧 Request payload:', JSON.stringify(payload, null, 2));
        
        // Check if response is HTML (404 page)
        if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
          return { 
            success: false, 
            message: 'Server error. Please try again later.',
            error: 'Server error'
          };
        }
        
        try {
          const errorJson = JSON.parse(errorText);
          console.error('📧 Parsed error JSON:', errorJson);
          const errorMessage = errorJson.message || errorJson.error || errorJson.msg || 'All fields are required';
          return { 
            success: false, 
            message: errorMessage,
            error: errorJson.error || 'API error',
            details: errorJson
          };
        } catch (e) {
          console.error('📧 Failed to parse error as JSON:', e);
          console.error('📧 Raw error text:', errorText);
          return { 
            success: false, 
            message: errorText.includes('required') ? errorText : `Server error (${response.status}). Please try again.`,
            error: 'API error',
            rawError: errorText
          };
        }
      }

      const data = await safeJsonParse(response);
      console.log('📧 Contact form success:', data);
      
      // Handle different response formats
      if (data && data.success !== undefined) {
        return {
          success: data.success,
          message: data.message || 'Thank you for contacting us! We will get back to you soon.',
          data: data
        };
      } else if (data && data.status === 200) {
        return {
          success: true,
          message: data.message || 'Thank you for contacting us! We will get back to you soon.',
          data: data
        };
      } else {
        return {
          success: true,
          message: 'Thank you for contacting us! We will get back to you soon.',
          data: data
        };
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { 
          success: false, 
          message: 'Server is not responding. Please contact support.',
          error: 'Server not running'
        };
      }
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return { 
          success: false, 
          message: 'Network error. Please check your internet connection and try again.',
          error: 'Network error'
        };
      }
      return { 
        success: false, 
        message: 'Error submitting form. Please try again.',
        error: error.message
      };
    }
  },

  // Submit Job Application
  async submitJobApplication(formData) {
    try {
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/web/job-application`;
      
      // Convert FormData to JSON (handle file uploads separately if needed)
      const jsonData = {};
      const fileData = new FormData();
      let hasFile = false;
      
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          fileData.append(key, value);
          hasFile = true;
        } else {
          jsonData[key] = value;
        }
      }

      // If there's a file, use FormData, otherwise use JSON
      if (hasFile) {
        const response = await fetch(apiUrl, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Accept': 'application/json'
          },
          credentials: 'omit',
          body: fileData
        });
        const data = await safeJsonParse(response);
        return data;
      } else {
        const response = await fetch(apiUrl, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'omit',
          body: JSON.stringify(jsonData)
        });
        const data = await safeJsonParse(response);
        return data;
      }
    } catch (error) {
      console.error('Error submitting job application:', error);
      return { success: false, message: 'Error submitting application. Please try again.' };
    }
  },

  // Submit Event Registration (Synergy Meet uses web/synergy-registration-enquiry with form-data)
  async submitEventRegistration(formData) {
    try {
      const entries = Object.fromEntries([...formData.entries()].map(([k, v]) => [k, typeof v === 'string' ? v : String(v)]));
      const eventType = (entries.event_type || '').trim();

      // Synergy Meet West Zone: backend API expects form-data (name, company_name, designation, mobile, email)
      if (eventType === 'synergy-meet') {
        const apiUrl = getBackendApiUrl('/web/synergy-registration-enquiry');
        const body = new FormData();
        body.append('name', (entries.name || '').trim());
        body.append('company_name', (entries.company_name || '').trim());
        body.append('designation', (entries.designation || '').trim());
        body.append('mobile', (entries.mobile || '').replace(/\D/g, ''));
        body.append('email', (entries.email || '').trim());

        const response = await fetch(apiUrl, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Accept': 'application/json' },
          credentials: 'omit',
          body
        });
        const data = await safeJsonParse(response);
        if (!response.ok) {
          return { success: false, message: (data && (data.message || data.error)) || 'Registration failed.', ...data };
        }
        return data && typeof data.success !== 'undefined' ? data : { success: true, ...data };
      }

      // Other events: legacy endpoint
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/web/event-registration`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(entries)
      });
      const data = await safeJsonParse(response);
      return data;
    } catch (error) {
      console.error('Error submitting event registration:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return {
          success: false,
          message: 'Server is not responding. Please contact support.',
          error: 'Server not running'
        };
      }
      return { success: false, message: 'Error submitting registration. Please try again.' };
    }
  },

  // Submit Elite Buyer Form → web/elite-buyer-enquiry (form-data: name, company_name, preferred_grade[], business_type[], city, mobile, email)
  async submitEliteBuyer(formData) {
    try {
      const apiUrl = getBackendApiUrl('/web/elite-buyer-enquiry');
      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Accept': 'application/json' },
        credentials: 'omit',
        body: formData
      });
      const data = await safeJsonParse(response);
      if (!response.ok) {
        return { success: false, message: (data && (data.message || data.error)) || 'Enquiry failed.', ...data };
      }
      return data && typeof data.success !== 'undefined' ? data : { success: true, ...data };
    } catch (error) {
      console.error('Error submitting elite buyer form:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { success: false, message: 'Server is not responding. Please contact support.', error: 'Server not running' };
      }
      return { success: false, message: 'Error submitting form. Please try again.' };
    }
  },

  // Submit Freight Request (uses freight.bsbfortrade.com)
  async submitFreightRequest(formData) {
    try {
      const baseUrl = FREIGHT_API_BASE;
      const apiUrl = `${baseUrl}/api/web/freight-request`;
      
      // Convert FormData to JSON
      const jsonData = {};
      for (let [key, value] of formData.entries()) {
        jsonData[key] = value;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(jsonData)
      });

      const data = await safeJsonParse(response);
      return data;
    } catch (error) {
      console.error('Error submitting freight request:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { 
          success: false, 
          message: 'Server is not responding. Please contact support.',
          error: 'Server not running'
        };
      }
      return { success: false, message: 'Error submitting request. Please try again.' };
    }
  },

  // Submit Credit Request
  async submitCreditRequest(formData) {
    try {
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/web/credit-request`;
      
      // Convert FormData to JSON
      const jsonData = {};
      for (let [key, value] of formData.entries()) {
        jsonData[key] = value;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(jsonData)
      });

      const data = await safeJsonParse(response);
      return data;
    } catch (error) {
      console.error('Error submitting credit request:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { 
          success: false, 
          message: 'Server is not responding. Please contact support.',
          error: 'Server not running'
        };
      }
      return { success: false, message: 'Error submitting request. Please try again.' };
    }
  },

  // Submit NBFC Interest Request (with mobile number) - legacy
  async submitNBFCInterestRequest(data) {
    try {
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/web/nbfc-interest-request`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(data)
      });

      const result = await safeJsonParse(response);
      return result;
    } catch (error) {
      console.error('Error submitting NBFC interest request:', error);
      return { success: false, message: 'Error submitting request. Please try again.' };
    }
  },

  // Credit Interest Save - when user enters mobile and clicks Submit in NBFC calculator
  // Payload: purchase_amount, required_credit_days, interest_rate, mobile, amount, approx_interest, total_payable_amount
  async saveCreditInterest(payload) {
    try {
      const apiUrl = getBackendApiUrl('/web/credit-interest-save');
      const body = {
        purchase_amount: Number(payload.purchase_amount) || 0,
        required_credit_days: Number(payload.required_credit_days) || 0,
        interest_rate: Number(payload.interest_rate) ?? 2.5,
        mobile: String(payload.mobile || '').replace(/\D/g, '').slice(0, 10),
        amount: Number(payload.amount) || 0,
        approx_interest: Number(payload.approx_interest) || 0,
        total_payable_amount: Number(payload.total_payable_amount) || 0
      };
      if (body.mobile.length !== 10) {
        return { success: false, message: 'Valid 10-digit mobile number is required.' };
      }
      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(body)
      });
      const data = await safeJsonParse(response);
      return data && typeof data === 'object' ? data : { success: true, data };
    } catch (error) {
      console.error('Error saving credit interest:', error);
      return { success: false, message: error.message || 'Error submitting. Please try again.' };
    }
  },

  // Submit Recycle Request (legacy FormData)
  async submitRecycleRequest(formData) {
    try {
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/web/recycle-request`;
      const jsonData = {};
      for (let [key, value] of formData.entries()) {
        jsonData[key] = value;
      }
      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(jsonData)
      });
      const data = await safeJsonParse(response);
      return data;
    } catch (error) {
      console.error('Error submitting recycle request:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { success: false, message: 'Server is not responding. Please contact support.', error: 'Server not running' };
      }
      return { success: false, message: 'Error submitting request. Please try again.' };
    }
  },

  // Sustainable Future – recycle offer: mobile + submit → web/recycle-enquiry
  // Form-data: mobile, grade, process_name, mfi, packaging, location
  async submitRecycleOfferRequest(payload) {
    try {
      const apiUrl = getBackendApiUrl('/web/recycle-enquiry');
      const mobile = (payload.mobile || '').toString().replace(/\D/g, '');
      if (mobile.length !== 10) {
        return { success: false, message: 'Please enter a valid 10-digit mobile number' };
      }
      const body = new FormData();
      body.append('mobile', mobile);
      body.append('grade', (payload.grade || '').toString().trim() || '');
      body.append('process_name', (payload.process_name || payload.process_type || '').toString().trim() || '');
      body.append('mfi', (payload.mfi != null && payload.mfi !== '') ? String(payload.mfi) : 'N/A');
      body.append('packaging', (payload.packaging || '').toString().trim() || '');
      body.append('location', (payload.location || '').toString().trim() || '');

      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Accept': 'application/json' },
        credentials: 'omit',
        body
      });
      const data = await safeJsonParse(response);
      if (!response.ok) {
        return { success: false, message: (data && (data.message || data.error)) || 'Enquiry failed.', ...data };
      }
      return data && typeof data.success !== 'undefined' ? data : { success: true, ...data };
    } catch (error) {
      console.error('Error submitting recycle enquiry:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { success: false, message: 'Server is not responding. Please contact support.', error: 'Server not running' };
      }
      return { success: false, message: 'Error submitting request. Please try again.' };
    }
  },

  // Get Available Slots for Demo Booking
  async getAvailableSlots(demoType, date) {
    try {
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/web/demo-slots`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify({
          demo_type: demoType,
          date: date
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Check if response is HTML (404 page)
        if (errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html')) {
          console.error('❌ IRM API: Server returned HTML. Node.js server is not running!');
          return { 
            success: false, 
            data: [],
            error: 'Server not running'
          };
        }
        return { success: false, data: [] };
      }

      // Check content type
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          console.error('❌ IRM API: Server returned HTML. Node.js server is not running!');
          return { 
            success: false, 
            data: [],
            error: 'Server not running'
          };
        }
      }

      const data = await safeJsonParse(response);
      return {
        success: true,
        data: data.data || data || []
      };
    } catch (error) {
      console.error('Error fetching available slots:', error);
      if (error.message.includes('HTML') || error.message.includes('not running')) {
        return { 
          success: false, 
          data: [],
          error: 'Server not running'
        };
      }
      return { success: false, data: [] };
    }
  },

  // Submit Demo Booking
  async submitDemoBooking(formData) {
    try {
      const baseUrl = getApiBaseUrl();
      const apiUrl = `${baseUrl}/api/web/demo-booking`;
      
      console.log('Submitting demo booking to:', apiUrl);
      
      // Convert FormData to JSON
      const jsonData = {};
      for (let [key, value] of formData.entries()) {
        jsonData[key] = value;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(jsonData)
      });

      console.log('Demo booking response status:', response.status, response.statusText);
      
      // Check if response is HTML (404 page) instead of JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          console.error('❌ Server returned HTML instead of JSON. Node.js server is not running!');
          return { 
            success: false, 
            message: 'Server is not responding. Please contact support or try again later.',
            error: 'Server not running'
          };
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API response not OK:', response.status, response.statusText);
        console.error('Error response:', errorText.substring(0, 200));
        return { 
          success: false, 
          message: `Server error (${response.status}). Please try again later.` 
        };
      }

      const data = await safeJsonParse(response);
      console.log('Demo booking success:', data);
      return data;
    } catch (error) {
      console.error('Error submitting demo booking:', error);
      if (error.message.includes('HTML') || error.message.includes('not running') || error.message.includes('Unexpected token') || error.message.includes('<!DOCTYPE')) {
        return { 
          success: false, 
          message: 'Server is not responding. The Node.js server may not be running. Please contact support.',
          error: 'Server not running'
        };
      }
      return { success: false, message: 'Error submitting booking. Please try again.' };
    }
  },

  // Submit Service Interest / Service Contact (I'm Interested form)
  // API: POST /web/serviceContact | form-data: company_name, service[] (multiple)
  async submitServiceInterest(formData) {
    try {
      const data = {};
      const serviceList = [];
      for (const [key, value] of formData.entries()) {
        const v = value.toString().trim();
        if (key === 'service[]') {
          serviceList.push(v);
        } else {
          data[key] = v;
        }
      }

      const nameValue = (data.name || '').trim();
      const mobileClean = (data.mobile || '').replace(/\D/g, '');
      const companyNameValue = (data.company || data.company_name || 'bsb').trim();

      if (mobileClean.length !== 10) {
        return { success: false, message: 'Please enter a valid 10-digit mobile number' };
      }
      if (!nameValue) {
        return { success: false, message: 'Name is required' };
      }
      if (!serviceList.length) {
        return { success: false, message: 'Please select at least one service' };
      }

      const apiUrl = getBackendApiUrl('/web/serviceContact');
      const body = new FormData();
      body.append('company_name', companyNameValue || 'bsb');
      body.append('name', nameValue);
      body.append('mobile', mobileClean);
      serviceList.forEach((s) => body.append('service[]', s));

      const response = await fetch(apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Accept': 'application/json' },
        credentials: 'omit',
        body
      });

      const responseText = await response.text();
      let responseData = null;
      if (responseText.trim()) {
        try {
          responseData = JSON.parse(responseText);
        } catch {
          responseData = { success: false, message: responseText || 'Server error' };
        }
      } else {
        responseData = { success: false, message: 'Empty response from server' };
      }

      if (response.ok) {
        return {
          success: responseData?.success !== false,
          message: responseData?.message || 'Thank you for your interest! We will contact you shortly.',
          data: responseData
        };
      }

      if (response.status === 400) {
        return {
          success: false,
          message: responseData?.message || 'All fields are required',
          error: 'HTTP 400',
          data: responseData
        };
      }

      return {
        success: false,
        message: responseData?.message || `Server error (${response.status}). Please try again.`,
        error: `HTTP ${response.status}`,
        data: responseData
      };
    } catch (error) {
      console.error('Error submitting service interest:', error);
      
      // Check for proxy/connection errors
      if (error.message.includes('ECONNREFUSED') || error.message.includes('Proxy error') || error.message.includes('Could not proxy')) {
        return { 
          success: false, 
          message: 'Backend server is not running. Please start the backend server on port 5050 and try again.',
          error: 'Backend server not running'
        };
      }
      
      if (error.message.includes('HTML') || error.message.includes('not running') || error.message.includes('Failed to fetch')) {
        return { 
          success: false, 
          message: 'Server is not responding. Please check your connection and try again.',
          error: 'Server not running'
        };
      }
      
      if (error.message.includes('NetworkError') || error.message.includes('network')) {
        return {
          success: false,
          message: 'Network error. Please check your internet connection and try again.',
          error: 'Network error'
        };
      }
      
      return { 
        success: false, 
        message: 'Error submitting interest. Please try again.',
        error: error.message
      };
    }
  },

  // Get Jobs - Currently returns empty array (no API endpoint available)
  async getJobs(filterPost = '', filterLocation = '') {
    // No API endpoint available, return empty array
    return { success: true, jobs: [] };
  },

  // Get News - Now handled directly in React component
  async getNews() {
    // News articles are now stored directly in the News.js component
    return { success: true, posts: [] };
  }
};

export default apiService;

